import { useEffect, useState } from 'react';
import axios from 'supports/api';

/** POST /v2/documents/token → the 60-second URL on the API host. */
const mintUrl = async (kind, id, params) => {
  const { data } = await axios.post('/v2/documents/token', {
    kind,
    id,
    params,
  });
  return `${process.env.REACT_APP_BASE_URL}${data.data.url}`;
};

const errorMessage = (err) =>
  err?.response?.data?.message || 'Could not open the document';

/**
 * Opens a PDF ('order' | 'invoice' | 'receipt' | 'po' | 'attendance'; a
 * receipt takes the payment id, a po the PO id, an attendance sheet the
 * program id plus `params` { sessions, lecturer, module }, the others the
 * transaction id) or a stored private upload ('file', no id, `params`
 * { path }) in a new tab, through a 60-second link from
 * POST /v2/documents/token. The tab opens before the request so the browser
 * still counts the click and does not block it.
 * Resolves true once the tab is on its way to the document.
 */
const openDocument = async (kind, id, params) => {
  const tab = window.open('', '_blank');

  if (!tab) {
    alert('Pop-up blocked. Allow pop-ups for this site to open PDFs.');
    return false;
  }
  tab.opener = null;

  try {
    tab.location.href = await mintUrl(kind, id, params);
    return true;
  } catch (err) {
    tab.close();
    alert(errorMessage(err));
    return false;
  }
};

export default openDocument;

/**
 * Opens a private upload in a new tab. `path` is the stored path exactly as
 * the API returns it ('/files/proof/PAY….jpg', '/poProof/POP-….pdf').
 * Replaces `<a href={BASE_URL + path} target="_blank">`.
 */
export const openFile = (path) => openDocument('file', undefined, { path });

/**
 * The 60-second URL for a private upload, for an <img>/<iframe> src. Mint it
 * when the element is about to show, not at page load for every row.
 */
export const getFileUrl = (path) => mintUrl('file', undefined, { path });

/**
 * getFileUrl as a hook: the URL while `path` is set, '' before it arrives
 * and when `path` is falsy. Pass null while a modal is closed so the link is
 * minted when it opens (the link expires after 60 s). A refused link
 * (403: not the viewer's file) is shown with alert, like other request errors.
 */
export const useFileUrl = (path) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    setUrl('');
    if (!path) return undefined;

    let stale = false;
    getFileUrl(path)
      .then((fileUrl) => !stale && setUrl(fileUrl))
      .catch((err) => !stale && alert(errorMessage(err)));
    return () => {
      stale = true;
    };
  }, [path]);

  return url;
};
